import { useEffect, useMemo, useState } from 'react';

import { ProcessingReferralRequestResult, ProcessingReferralRow, SubmittedReferralSessionResult } from '../types';

const ACTIVE_POLL_INTERVAL_MS = 2000;
const IDLE_POLL_INTERVAL_MS = 30000;
const DELAYED_AFTER_MS = 120000;

type UseProcessingReferralRowsOptions = {
  readonly error?: unknown;
  readonly processingReferralRequests: ProcessingReferralRequestResult[];
  readonly refetch: () => void;
  readonly sessions: Pick<SubmittedReferralSessionResult, 'referralRequestId'>[];
};

const getPollInterval = (rows: ProcessingReferralRow[], error: unknown): number | undefined => {
  if (rows.some((row) => !row.delayed)) return ACTIVE_POLL_INTERVAL_MS;
  if (rows.length > 0 || error) return IDLE_POLL_INTERVAL_MS;
  return undefined;
};

const useProcessingReferralRows = ({
  error,
  processingReferralRequests,
  refetch,
  sessions,
}: UseProcessingReferralRowsOptions): ProcessingReferralRow[] => {
  const [now, setNow] = useState(() => Date.now());

  const rows = useMemo(() => {
    const sentReferralRequestIds = new Set(sessions.map((session) => session.referralRequestId));

    return processingReferralRequests
      .filter((processingReferralRequest) => !sentReferralRequestIds.has(processingReferralRequest.id))
      .map((processingReferralRequest) => ({
        ...processingReferralRequest,
        delayed: now - new Date(processingReferralRequest.submittedAt).getTime() >= DELAYED_AFTER_MS,
        kind: 'processing' as const,
      }));
  }, [now, processingReferralRequests, sessions]);

  const pollInterval = getPollInterval(rows, error);

  useEffect(() => {
    if (pollInterval === undefined) return;

    const interval = setInterval(() => {
      refetch();
      setNow(Date.now());
    }, pollInterval);

    return () => clearInterval(interval);
  }, [pollInterval, refetch]);

  return rows;
};

export { ACTIVE_POLL_INTERVAL_MS, DELAYED_AFTER_MS, IDLE_POLL_INTERVAL_MS, useProcessingReferralRows };
